import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import css from './RadioCardGroup.module.css';
import { Label, ValidationMessage } from '../../components';
import {
  StackedColumn,
  StackedColumnSpacing,
  StackedRow,
  StackedRowSpacing,
  WrappingContainer,
  WrappingContainerSpacing,
} from '../../../containers';
import { Radio } from '../radio';
import { Card } from '../../../card';
import { H3, Paragraph } from '../../../text';
import clsx from 'clsx';
import { ErrorBanner } from '../../../messageBanners';
import { FieldState } from '../../models';
import { RadioCardOptionOrientation } from './RadioCardOptionOrientation';
import { RadioCardSizing } from './RadioCardSizing';

export interface RadioCardOption {
  value: string;
  label: string;
  description?: string;
  associatedChild?: ReactNode;
}

export interface RadioCardGroupProps {
  label?: string;
  required?: boolean;
  disabled?: boolean;
  validate?: boolean;
  optionalText?: string;
  optionalNode?: ReactNode;
  groupName: string;
  radioOptions: RadioCardOption[];
  selectedValue: string;
  orientation?: RadioCardOptionOrientation;
  sizing?: RadioCardSizing;
  fullWidth?: boolean;
  errorMessage?: string;
  onChange: (selectedvalue: string) => void;
}

const RadioCardGroup: React.FC<RadioCardGroupProps> = (
  props: RadioCardGroupProps
) => {
  const {
    orientation = RadioCardOptionOrientation.Vertical,
    sizing = RadioCardSizing.Large,
  } = props;
  const [currentState, setCurrentState] = useState<FieldState>(
    FieldState.Inactive
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    props.errorMessage
  );

  useEffect(() => {
    if (props.disabled) {
      setCurrentState(FieldState.Disabled);
      // } else if (props.hasFocus) {
      //   setCurrentState(FieldState.Active);
    } else if (props.errorMessage) {
      setCurrentState(FieldState.Error);
    } else {
      setCurrentState(FieldState.Inactive);
    }
  }, [props.disabled, props.errorMessage]);

  // destructured to make lint happy
  const {
    required,
    validate,
    errorMessage: errMsg,
    selectedValue,
    label,
  } = props;
  const checkIfRequired = useCallback(() => {
    if (required && validate && selectedValue === '') {
      setErrorMessage(`${label} is required`);
    } else {
      setErrorMessage(errMsg);
    }
  }, [required, validate, selectedValue, errMsg, label]);

  useEffect(() => {
    if (props.validate) {
      checkIfRequired();
    } else {
      setErrorMessage(props.errorMessage);
    }
  }, [checkIfRequired, props.validate, props.errorMessage]);

  const containerRef = useRef<HTMLDivElement>(null);

  const onContainerClicked = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    value: string
  ) => {
    e.stopPropagation();
    onCheckChange(value);
  };

  const onCheckChange = (value: string) => {
    if (props.disabled) {
      return;
    }

    const itemIndex = props.radioOptions.findIndex(
      (option) => option.value === value
    );
    props.onChange(value);

    if (itemIndex >= 0) {
      const cardDiv = containerRef.current?.querySelectorAll(
        `div.${css.cardContainer}`
      )[itemIndex] as HTMLDivElement;
      const selectionInput = cardDiv?.querySelector(
        'input'
      ) as HTMLInputElement;
      selectionInput?.focus();
    }
  };

  const radioCards = props.radioOptions.map(
    (option: RadioCardOption, index) => {
      const isChecked = props.selectedValue === option.value;

      return (
        <div key={index}>
          <div
            className={clsx(
              css.cardContainer,
              sizing === RadioCardSizing.Small
                ? css.cardContainerSmall
                : sizing === RadioCardSizing.Large
                ? css.cardContainerLarge
                : null,
              props.fullWidth && css.cardContainerFullWidth
            )}
            onClick={(e) => onContainerClicked(e, option.value)}
          >
            <Card
              className={clsx(
                css.optionCard,
                isChecked ? css.optionCardSelected : null,
                errorMessage ? css.optionCardError : null,
                props.disabled ? css.optionCardDisabled : null
              )}
            >
              <StackedRow fillHeight={true}>
                <div className={css.radioContainer}>
                  <Radio
                    groupName={props.groupName}
                    checked={isChecked}
                    disabled={props.disabled}
                    showAsError={errorMessage ? true : false}
                    hideFocus={true}
                    value={option.value}
                    onCheckChange={() => onCheckChange(option.value)}
                  />
                </div>
                <StackedColumn
                  spacing={StackedColumnSpacing.Tight}
                  fillHeight={true}
                >
                  <div className={css.titleContainer}>
                    <H3 className={css.cardTitle} text={option.label} />
                  </div>
                  {option.description && sizing !== RadioCardSizing.Small ? (
                    <div className={css.descriptionContainer}>
                      <Paragraph
                        className={css.cardDescription}
                        text={option.description}
                      />
                    </div>
                  ) : null}
                </StackedColumn>
              </StackedRow>
            </Card>
          </div>
          {option.associatedChild ? (
            <div className={css.optionChildContainer}>
              {option.associatedChild}
            </div>
          ) : null}
        </div>
      );
    }
  );

  return (
    <div ref={containerRef}>
      <StackedColumn spacing={StackedColumnSpacing.Normal}>
        {props.label ? (
          <Label
            state={currentState}
            required={props.required}
            text={props.label}
          />
        ) : null}
        {props.optionalText ? (
          <Paragraph
            className={css.optionalText}
            small={true}
            text={props.optionalText}
          />
        ) : null}
        {props.optionalNode ? (
          <div className={css.optionalText}>{props.optionalNode}</div>
        ) : null}
        {sizing !== RadioCardSizing.Small && errorMessage ? (
          <div className={css.messageContainer}>
            <ErrorBanner text={errorMessage ?? ''} />
          </div>
        ) : null}
        {orientation === RadioCardOptionOrientation.Wrap ? (
          <WrappingContainer spacing={WrappingContainerSpacing.Normal}>
            {radioCards}
          </WrappingContainer>
        ) : orientation === RadioCardOptionOrientation.Horizontal ? (
          <StackedRow spacing={StackedRowSpacing.Normal}>
            {radioCards}
          </StackedRow>
        ) : (
          <StackedColumn spacing={StackedColumnSpacing.Normal}>
            {radioCards}
          </StackedColumn>
        )}
        {sizing === RadioCardSizing.Small && errorMessage ? (
          <div className={css.messageContainer}>
            <ValidationMessage errorMessage={errorMessage} />
          </div>
        ) : null}
      </StackedColumn>
    </div>
  );
};

export default RadioCardGroup;
